import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowUpRight, ExternalLink, Clock } from "lucide-react";
import { proxyImg } from "@/lib/api";

/* ---------------- inline markdown ---------------- */
/* ---------------- inline markdown: bold, italic, links, code ---------------- */
function renderInline(text) {
  const str = String(text);
  const re = /(\*\*[^*]+\*\*|\*[^*\n]+\*|\[[^\]]+\]\([^)]+\)|`[^`]+`)/g;
  const out = [];
  let last = 0, m, key = 0;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) out.push(<span key={key++}>{str.slice(last, m.index)}</span>);
    const tok = m[0];
    if (tok.startsWith("**")) out.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) out.push(<code key={key++} className="px-1 py-0.5 rounded bg-white/10 text-klyroo-cyan text-[0.9em]">{tok.slice(1, -1)}</code>);
    else if (tok.startsWith("*")) out.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    else {
      const lm = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      out.push(<a key={key++} href={lm[2]} target="_blank" rel="noopener noreferrer" className="text-klyroo-cyan underline underline-offset-2 hover:text-white">{lm[1]}</a>);
    }
    last = m.index + tok.length;
  }
  if (last < str.length) out.push(<span key={key++}>{str.slice(last)}</span>);
  return out;
}

export function SectionRenderer({ sections }) {
  return (
    <div className="prose-klyroo">
      {(sections || []).map((s, i) => (
        <section key={i} className="mb-9" id={slug(s.heading)}>
          {s.heading && <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight">{s.heading}</h2>}
          {renderBlocks(s.content)}
        </section>
      ))}
    </div>
  );
}

// Reusable markdown block renderer (used by public pages AND the CMS live preview).
export function Markdown({ content, className = "" }) {
  return <div className={`prose-klyroo ${className}`}>{renderBlocks(content)}</div>;
}

function renderBlocks(content) {
  const blocks = String(content || "").split(/\n\n+/);
  return blocks.map((b, i) => {
    const raw = b.replace(/\n+$/, "");
    if (!raw.trim()) return null;
    const lines = raw.split("\n");
    const img = raw.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (img) return <LazyImage key={i} src={img[2]} alt={img[1]} className="rounded-xl my-6" />;
    if (/^###\s+/.test(raw)) return <h3 key={i} className="font-display text-xl sm:text-2xl font-bold text-white mt-6 mb-3">{renderInline(raw.replace(/^###\s+/, ""))}</h3>;
    if (/^##\s+/.test(raw)) return <h2 key={i} className="font-display text-2xl font-bold text-white mt-6 mb-3">{renderInline(raw.replace(/^##\s+/, ""))}</h2>;
    if (lines.every((l) => l.trim().startsWith(">"))) {
      return <blockquote key={i} className="border-l-2 border-klyroo-cyan/60 pl-4 italic text-slate-300 my-5">{renderInline(lines.map((l) => l.replace(/^\s*>\s?/, "")).join(" "))}</blockquote>;
    }
    if (lines.every((l) => l.trim().startsWith("- "))) {
      return <ul key={i}>{lines.map((l, j) => <li key={j}>{renderInline(l.replace(/^\s*-\s*/, ""))}</li>)}</ul>;
    }
    if (lines.every((l) => /^\s*\d+\.\s+/.test(l))) {
      return <ol key={i} className="list-decimal pl-5 space-y-1">{lines.map((l, j) => <li key={j}>{renderInline(l.replace(/^\s*\d+\.\s*/, ""))}</li>)}</ol>;
    }
    return <p key={i}>{renderInline(raw)}</p>;
  });
}

const slug = (s) => String(s).toLowerCase().replace(/[^\w]+/g, "-").replace(/^-|-$/g, "");

// Format an ISO date/datetime for display, e.g. "Aug 26, 2026". Falls back to raw value.
export function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/* ---------------- media ---------------- */
export function LazyImage({ src, alt, className = "", ratio = "16/9" }) {
  const url = src ? proxyImg(src) : null;
  return (
    <div className={`relative overflow-hidden bg-klyroo-surface ${className}`} style={{ aspectRatio: ratio }}>
      {url ? (
        <>
          <img src={url} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover scale-110 blur-xl opacity-60" />
          <img src={url} alt={alt} decoding="async" className="relative h-full w-full object-contain" />
        </>
      ) : (
        <div className="h-full w-full flex items-center justify-center text-slate-700"><Star size={20} /></div>
      )}
    </div>
  );
}

export function ToolLogo({ name, logo, size = 44 }) {
  const [err, setErr] = useState(false);
  const initials = name.replace(/[^a-zA-Z0-9 ]/g, "").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  if (err || !logo) {
    return (
      <div className="flex items-center justify-center rounded-xl font-display font-bold text-klyroo-cyan bg-white/5 border border-white/10"
        style={{ width: size, height: size, fontSize: size * 0.34 }}>
        {initials}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center rounded-xl bg-white overflow-hidden border border-white/10" style={{ width: size, height: size }}>
      <img src={proxyImg(logo)} alt={`${name} logo`} onError={() => setErr(true)} className="object-contain" style={{ width: size * 0.72, height: size * 0.72 }} />
    </div>
  );
}

/* ---------------- atoms ---------------- */
export function Rating({ value, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-sm ${className}`}>
      <Star size={13} className="fill-klyroo-amber text-klyroo-amber" />
      <span className="text-white font-semibold">{Number(value).toFixed(1)}</span>
    </span>
  );
}

const pricingColor = { Free: "text-klyroo-green border-klyroo-green/30 bg-klyroo-green/10", Freemium: "text-klyroo-cyan border-klyroo-cyan/30 bg-klyroo-cyan/10", Paid: "text-klyroo-amber border-klyroo-amber/30 bg-klyroo-amber/10" };
export function PricingBadge({ model }) {
  return <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full border ${pricingColor[model] || pricingColor.Freemium}`}>{model}</span>;
}

export function Overline({ children, className = "" }) {
  return <div className={`overline ${className}`}>{children}</div>;
}

export function CategoryTag({ children, to }) {
  const inner = <span className="overline text-klyroo-cyan">{children}</span>;
  return to ? <Link to={to} className="link-underline">{inner}</Link> : inner;
}

export function Meta({ children }) {
  return <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 font-mono">{children}</span>;
}

/* ---------------- cards ---------------- */
export function ArticleCard({ a, size = "md" }) {
  const big = size === "lg";
  return (
    <Link to={`/article/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group block card-surface rounded-2xl overflow-hidden hover-raise">
      <LazyImage src={a.image} alt={a.image_alt || a.title} ratio={big ? "16/9" : "16/10"} />
      <div className="p-5">
        <div className="flex items-center gap-3 mb-2">
          <span className="overline text-klyroo-cyan">{a.category}</span>
          <span className="text-slate-600">·</span>
          <Meta><Clock size={12} /> {a.reading_time} min</Meta>
        </div>
        <h3 className={`font-display font-bold text-white leading-snug tracking-tight group-hover:text-klyroo-cyan transition-colors ${big ? "text-2xl sm:text-3xl" : "text-lg"}`}>{a.title}</h3>
        <p className={`text-slate-400 mt-2 ${big ? "text-base" : "text-sm line-clamp-2"}`}>{a.subtitle}</p>
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 font-mono">
          <span>{a.content_type}</span><span>·</span><span>{a.author}</span>
        </div>
      </div>
    </Link>
  );
}

export function ToolCard({ t }) {
  return (
    <Link to={`/tools/${t.slug}`} data-testid={`tool-card-${t.slug}`} className="group card-surface rounded-2xl p-5 hover-raise flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <ToolLogo name={t.name} logo={t.logo} />
        <div className="flex items-center gap-2">
          {t.is_new && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-klyroo-violet/20 text-klyroo-violet border border-klyroo-violet/30">NEW</span>}
          <Rating value={t.rating} />
        </div>
      </div>
      <h3 className="font-display font-bold text-white text-lg mt-4 group-hover:text-klyroo-cyan transition-colors">{t.name}</h3>
      <p className="text-sm text-slate-400 mt-1.5 line-clamp-2 flex-1">{t.tagline}</p>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
        <span className="text-[11px] font-mono text-slate-500">{t.category.replace("AI ", "")}</span>
        <PricingBadge model={t.pricing_model} />
      </div>
    </Link>
  );
}

export function hueFromSlug(s) {
  let h = 0;
  for (const ch of String(s)) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

export function ComparisonVisual({ c, height = "h-44", detail = false }) {
  const h = hueFromSlug(c.slug);
  const h2 = (h + 48) % 360;
  const logos = (c.logos || []).filter(Boolean).slice(0, 4);
  const names = (c.items || []).filter(Boolean);
  const showLogos = logos.length > 0 && logos.length >= names.length; // fall back to name chips if any product lacks a logo
  const bg = `radial-gradient(120% 120% at 85% 15%, hsl(${h2} 80% 22% / .9), transparent 55%), linear-gradient(140deg, hsl(${h} 60% 16%), #0A0C14 78%)`;
  return (
    <div className={`relative ${detail ? "h-56 sm:h-72" : height} overflow-hidden`} style={{ background: bg }}>
      <div className="absolute inset-0 opacity-40 klyroo-grid" />
      <div className="relative h-full flex flex-col items-center justify-center gap-3 px-4">
        {showLogos ? (
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            {logos.map((l, i) => (
              <div key={i} className="flex items-center">
                <div className={`rounded-2xl bg-white ${detail ? "w-16 h-16 p-2.5" : "w-12 h-12 p-2"} flex items-center justify-center shadow-lg`}>
                  <img src={proxyImg(l)} alt="" onError={(e) => (e.currentTarget.parentElement.style.display = "none")} className="w-full h-full object-contain" />
                </div>
                {i < logos.length - 1 && <span className="mx-1 sm:mx-1.5 font-display font-extrabold text-white/70 text-xs sm:text-sm">VS</span>}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {(c.items || []).slice(0, 4).map((it, i) => (
              <span key={i} className="flex items-center">
                <span className={`font-display font-bold text-white ${detail ? "text-lg" : "text-sm"} px-3 py-1.5 rounded-xl bg-white/10 border border-white/15 backdrop-blur`}>{it}</span>
                {i < (c.items || []).length - 1 && i < 3 && <span className="mx-1.5 font-display font-extrabold text-klyroo-cyan text-xs">VS</span>}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
    </div>
  );
}

export function ComparisonCard({ c }) {
  return (
    <Link to={`/comparisons/${c.slug}`} data-testid={`comparison-card-${c.slug}`} className="group card-surface rounded-2xl overflow-hidden hover-raise">
      <ComparisonVisual c={c} />
      <div className="p-5">
        <span className="overline text-klyroo-cyan">{c.category}</span>
        <h3 className="font-display font-bold text-white text-lg mt-2 group-hover:text-klyroo-cyan transition-colors leading-snug">{c.title}</h3>
        <p className="text-xs text-slate-500 mt-3 font-mono">Updated {formatDate(c.updated_at)}</p>
      </div>
    </Link>
  );
}

export function ModelCard({ m }) {
  return (
    <Link to={`/ai-models/${m.slug}`} data-testid={`model-card-${m.slug}`} className="group card-surface rounded-2xl p-5 hover-raise">
      <div className="flex items-center justify-between">
        <ToolLogo name={m.company} logo={m.logo} size={40} />
        <Rating value={m.rating} />
      </div>
      <h3 className="font-display font-bold text-white text-lg mt-3 group-hover:text-klyroo-cyan transition-colors">{m.name}</h3>
      <p className="text-sm text-slate-400 mt-1">{m.company}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Chip>{m.context}</Chip><Chip>{m.model_type}</Chip>
      </div>
    </Link>
  );
}

export function Chip({ children }) {
  return <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-slate-300">{children}</span>;
}

/* ---------------- signature blocks ---------------- */
export function IntelligenceBox({ intel }) {
  const rows = [
    ["What happened?", intel?.happened],
    ["Why does it matter?", intel?.matters],
    ["Who is affected?", intel?.affected],
    ["What should you know?", intel?.know],
    ["What's next?", intel?.next],
  ].filter(([, a]) => a && String(a).trim());
  if (!rows.length) return null;
  return (
    <div data-testid="klyroo-intelligence" className="my-10 rounded-2xl overflow-hidden border border-klyroo-cyan/25 bg-gradient-to-br from-klyroo-cyan/[0.08] to-transparent">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-klyroo-cyan/20 bg-klyroo-cyan/5">
        <span className="w-2 h-2 rounded-full bg-klyroo-cyan animate-pulse" />
        <span className="font-display font-extrabold tracking-tight text-white">KLYROO INTELLIGENCE</span>
      </div>
      <div className="p-6 space-y-5">
        {rows.map(([q, a], i) => (
          <div key={i} className="flex gap-4">
            <div className="w-1 rounded-full bg-klyroo-cyan/40 shrink-0" />
            <div>
              <div className="overline text-klyroo-cyan mb-1">{q}</div>
              <p className="text-slate-200 leading-relaxed text-[15px]">{a}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function KeyTakeaways({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="rounded-2xl card-surface p-6 my-8">
      <div className="overline text-klyroo-cyan mb-3">Key Takeaways</div>
      <ul className="prose-klyroo mb-0">{items.map((t, i) => <li key={i}>{renderInline(t)}</li>)}</ul>
    </div>
  );
}

export function VerdictCard({ score, summary, title = "KLYROO Verdict" }) {
  if (!summary) return null;
  const pct = (score / 10) * 100;
  return (
    <div data-testid="verdict-card" className="my-10 card-surface rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row gap-7 items-start cyan-glow">
      <div className="relative shrink-0" style={{ width: 116, height: 116 }}>
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
          <circle cx="60" cy="60" r="52" fill="none" stroke="#00F0FF" strokeWidth="10" strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * 2 * Math.PI * 52} ${2 * Math.PI * 52}`} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display font-extrabold text-3xl text-white">{Number(score).toFixed(1)}</span>
          <span className="text-[10px] font-mono text-slate-400">/ 10</span>
        </div>
      </div>
      <div>
        <div className="overline text-klyroo-cyan mb-2">{title}</div>
        <p className="text-slate-200 leading-relaxed">{summary}</p>
      </div>
    </div>
  );
}

export function ProsCons({ pros, cons }) {
  if ((!pros || !pros.length) && (!cons || !cons.length)) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-4 my-8">
      <div className="rounded-2xl border border-klyroo-green/25 bg-klyroo-green/[0.06] p-5">
        <div className="overline text-klyroo-green mb-3">Pros</div>
        <ul className="space-y-2">{(pros || []).map((p, i) => <li key={i} className="text-sm text-slate-200 flex gap-2"><span className="text-klyroo-green">+</span>{p}</li>)}</ul>
      </div>
      <div className="rounded-2xl border border-klyroo-red/25 bg-klyroo-red/[0.06] p-5">
        <div className="overline text-klyroo-red mb-3">Cons</div>
        <ul className="space-y-2">{(cons || []).map((p, i) => <li key={i} className="text-sm text-slate-200 flex gap-2"><span className="text-klyroo-red">−</span>{p}</li>)}</ul>
      </div>
    </div>
  );
}

export function ComparisonTable({ columns, rows }) {
  return (
    <div className="my-8 overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-sm border-collapse min-w-[560px]">
        <thead>
          <tr className="bg-white/5">
            {columns.map((c, i) => (
              <th key={i} className={`text-left p-4 font-display font-bold ${i === 0 ? "text-slate-400 sticky left-0 bg-klyroo-card z-10" : "text-white"}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/8 hover:bg-white/[0.03]">
              <td className="p-4 font-medium text-slate-300 sticky left-0 bg-klyroo-bg z-10">{r.feature}</td>
              {r.values.map((v, j) => <td key={j} className="p-4 text-slate-200">{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function QuickAnswer({ best }) {
  if (!best || !Object.keys(best).length) return null;
  return (
    <div className="rounded-2xl card-surface p-6 my-6 border-l-2 border-klyroo-cyan">
      <div className="overline text-klyroo-cyan mb-3">Quick Answer</div>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
        {Object.entries(best || {}).map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4 py-1.5 border-b border-white/5">
            <span className="text-sm text-slate-400">{k}</span>
            <span className="font-display font-semibold text-white text-right">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FAQ({ faqs }) {
  const [open, setOpen] = useState(0);
  if (!faqs || !faqs.length) return null;
  return (
    <div className="my-10">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-5 tracking-tight">Frequently Asked Questions</h2>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <div key={i} className="card-surface rounded-xl overflow-hidden">
            <button data-testid={`faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 p-5 text-left">
              <span className="font-display font-semibold text-white">{f.q}</span>
              <span className={`text-klyroo-cyan transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
            </button>
            {open === i && <div className="px-5 pb-5 text-slate-300 leading-relaxed text-[15px]">{f.a}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="flex items-center gap-2 text-xs font-mono text-slate-500 mb-6 flex-wrap">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-2">
          {it.to ? <Link to={it.to} className="hover:text-klyroo-cyan">{it.label}</Link> : <span className="text-slate-300">{it.label}</span>}
          {i < items.length - 1 && <span>/</span>}
        </span>
      ))}
    </nav>
  );
}

export function SectionHeader({ overline, title, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6">
      <div>
        <Overline className="mb-2">{overline}</Overline>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function Loader() {
  return <div className="min-h-[50vh] flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-klyroo-cyan border-t-transparent animate-spin" /></div>;
}

// Fades/slides its content in the first time it scrolls into view (staggers children if `stagger`).
export function Reveal({ children, className = "", stagger = false }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } },
      { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`reveal ${stagger ? "reveal-stagger" : ""} ${vis ? "is-visible" : ""} ${className}`}>{children}</div>;
}

export { ArrowUpRight, ExternalLink };
